/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import format from 'date-format';
import datePickerSupported from 'utils/datePickerSupported';

const supportsDatepicker = datePickerSupported();

const isoFormat = 'yyyy-MM-dd';
const isoPattern = '\\d{4}-(0[1-9]|1[012])-(0[1-9]|[12]\\d|3[01])';

function getDisplayDate(string, pattern) {
  const re = new RegExp(isoPattern);
  if (string.match(re)) {
    const date = format.parse(isoFormat, string);
    return format(pattern, date);
  }

  return string;
}

function getIsoDate(string, pattern) {
  const date = format.parse(pattern, string);
  return format(isoFormat, date);
}

/**
 * This is the Date component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function DateField({ value, format, pattern, onChange, ...props }) {
  const [date, setDate] = useState(getDisplayDate(value, format));

  useEffect(() => {
    if (!supportsDatepicker) {
      const re = new RegExp(pattern);
      if (date.match(re)) {
        onChange({ target: { value: getIsoDate(date, format) } });
      }
    }
  }, [pattern, format, date]);

  if (supportsDatepicker) {
    return <input {...props} value={value} onChange={onChange} />;
  }

  return (
    <input
      {...props}
      type="text"
      value={date}
      onChange={({ target: { value } }) => {
        setDate(value);
      }}
    />
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
DateField.propTypes = {
  value: PropTypes.string,
  format: PropTypes.string.isRequired,
  pattern: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
DateField.defaultProps = {
  value: '',
  format: 'yyyy-MM-dd',
  pattern: isoPattern,
  onChange: () => {},
};

export default DateField;
